import { Subscription } from 'effector';
import axios from 'axios';
import { createPopper } from '@popperjs/core';
import { onCommentsReady } from '@nx-frontend/features/web-editor/util';
import { loadCss, loadJs } from '@nx-frontend/features/web-editor/util/util';
import { $webEditorComments } from '@nx-frontend/shared/store/web-editor-store';
import {
  Blot,
  CommentPluginContext,
  CommentUser,
  EditorInstance,
  OpenThreadResult,
  ResoveThreadEvent,
  SelectedBlotRange,
  ThreadContext,
  ThreadEditor,
  ThreadInfo,
  ThreadParams,
  ThreadTargetParams,
} from '@nx-frontend/shared/types';
import './style.scss';

export class CommentManager {
  public enabled = true;
  public inlineComments = true;
  public renderCounters = true;
  public tableCellComments = true;
  public countersStyle = 'default';
  public TEMP_INLINE_THREAD_CLASS = 'temporary-inline-thread-node-origin';
  private threadsInfo: ThreadEditor[] | undefined;
  private popupContainer: HTMLDivElement | null = null;
  private scope: {
    context: CommentPluginContext | null;
    threadContext: ThreadContext | null;
  } = {
    context: null,
    threadContext: null,
  };
  private threadResolver!: (threadId: string) => void;

  private commentsLoaded = false;
  private targetNode: HTMLElement | null = null;

  constructor(
    private workspaceId: string,
    private noteId: string,
    private gatewayUrl: string,
    private sessionId: string,
    private editorInstanceGetter: () => EditorInstance | null,
    private staticAssetPrefix: string,
    private user: CommentUser,
    private readonly: boolean,
    private isWorkspaceMember: boolean,
    private shareId = '',
    private securityKey = '',
    private commentsBundleJsUrl = '',
    private tasksBundleCssUrl = '',
    private scrollingContainer: string | HTMLElement = '',
    private disableOpenComments = false
  ) {
    this.enabled = this.isWorkspaceMember;
    const popupContainerId = 'comment-manager-popup-container';

    this.popupContainer = document.body.querySelector(`#${popupContainerId}`);
    if (!this.popupContainer) {
      this.popupContainer = document.createElement('div');
      this.popupContainer.id = popupContainerId;
      if (this.scrollingContainer) {
        if (typeof this.scrollingContainer === 'string') {
          document.getElementById(scrollingContainer as string)?.appendChild(this.popupContainer);
        } else {
          this.scrollingContainer.appendChild(this.popupContainer);
        }
      } else {
        document.body.appendChild(this.popupContainer);
      }
    }
  }

  private webEditorCommentsSubscription: Subscription | null = null;

  public onCommentsLoaded: (() => void) | null = null;
  public setOnCommentsLoaded = (callback: () => void) => {
    if (this.commentsLoaded) {
      callback();
      return;
    }
    this.onCommentsLoaded = callback;
  };

  // функция устанавливает threadId для открытия комментариев по ссылке
  public setThreadIdToOpen = (threadId: string) => {
    const thread = this.threadsInfo?.find((item) => item.threadId === threadId);
    this.threadIdToOpen = threadId;

    if (thread) {
      this.commentsLoaded = true;
      this.onCommentsLoaded && this.onCommentsLoaded();
    }
  };

  public getPluginInstance = () => {
    return this;
  };

  private threadIdToOpen: string | null = null;
  private resizeObserver: ResizeObserver | null = null;

  public initPluginOnEditorAvailable = () => {
    // load comments bundle
    loadJs({ url: this.commentsBundleJsUrl, id: 'comments-bundle-js' });
    // load css for comments from tasks bundle
    loadCss({ url: this.tasksBundleCssUrl, id: 'tasks-bundle-css' });

    onCommentsReady(() => {
      const setupApiEvent = new CustomEvent('ng2:attachments:setup-api', {
        detail: {
          gatewayUrl: this.gatewayUrl,
          gatewayToken: this.sessionId,
          staticAssetPrefix: this.staticAssetPrefix,
        },
      });
      document.dispatchEvent(setupApiEvent);
    });

    if (!this.webEditorCommentsSubscription) {
      this.webEditorCommentsSubscription = $webEditorComments.watch((payload) => {
        this.refreshTreads();
      });
    }
  };

  public destroy() {
    this.webEditorCommentsSubscription?.unsubscribe();
    this.destroyExistedPopup();
    this.popupContainer?.remove();
    this.resizeObserver?.disconnect();
  }

  private refreshTreads = async () => {
    const isTextThread = (info: ThreadInfo) => ['text', 'blot', 'image-point', 'cell'].includes(info.thread.target);
    const isExists = (info: ThreadInfo) => !info.notFound;
    const rawInfo = $webEditorComments.getState() as ThreadInfo[];
    const [workspaceId, noteGlobalId] = this.editorInstanceGetter()?.options.serverRoom?.split('_') || [];
    this.threadsInfo = rawInfo
      // filter threads by noteId and workspaceId
      .filter((info) => info.thread.noteGlobalId === noteGlobalId && info.thread.workspaceId === workspaceId)
      .filter(isTextThread)
      .filter(isExists)
      .map((thread: ThreadInfo) => {
        return {
          attachmentId: thread.thread.attachmentId,
          threadId: thread.thread.globalId,
          blotId: thread.thread.blotId,
          count: thread.comments,
          target: thread.thread.target,
          x: thread.targetDetails?.x,
          y: thread.targetDetails?.y,
          color: thread.targetDetails?.color,
          unreadComments: thread.unreadComments || 0,
        };
      });
    this.editorInstanceGetter()?.plugins.commentManager?.setThreads(this.threadsInfo);

    const thread = this.threadsInfo.find((item) => item.threadId === this.threadIdToOpen);
    if (!this.commentsLoaded && thread) {
      this.commentsLoaded = true;
      this.onCommentsLoaded && this.onCommentsLoaded();
    }
  };

  public openComments = async (blotId: string, params: ThreadParams) => {
    if (this.disableOpenComments) return;
    const context = {
      orgId: this.user.orgId,
      userId: this.user.id,
      userRole: this.user.role,
      workspaceId: this.workspaceId,
    };

    const threadContext = {
      targetId: blotId,
      noteId: this.noteId,
      targetType: params.target ?? null,
      title: params.text,
      threadId: params.threadId ?? null,
      targetParams: params.targetParams ?? null,
    };
    this.scope.context = context;
    this.scope.threadContext = threadContext;

    this.updatePopup().then(() => {
      // fix bag with white screen (https://nimbusweb.atlassian.net/browse/NIM-32711)
      setTimeout(async () => {
        const html = document.querySelector('html');
        if (html && html.scrollTop > 0) {
          html.scrollTop = 0;
          this.destroyExistedPopup();
          this.openComments(blotId, params);
        }
      }, 0);
    });
    return new Promise((resolve: (result: OpenThreadResult) => void) => {
      this.threadResolver = (threadId) => {
        const openResult = { threadId, isNew: !params.threadId };
        resolve(openResult);
      };
    });
  };

  public openCommentsByThreadId = (initOpenThreadId: string, commentId?: string) => {
    if (this.threadsInfo) {
      const thread = this.threadsInfo.find((thread) => thread.threadId === initOpenThreadId);
      if (thread) {
        const rootBlotText = this.getRootBlotText(initOpenThreadId) ?? '';
        this.openComments(thread.blotId, {
          threadId: thread.threadId,
          text: rootBlotText,
        });

        // в заметке могут быть таск-листы или канбан и они загружаются не сразу, если коммент открыт
        // то загрузка тасков может сдвинуть контент вниз тем самы попап окажется не там где блот с комментарием
        // с помощью ResizeObserver-а пока тред открыт следим за изменением высоты редактора
        this.resizeObserver?.disconnect();
        const container = this.editorInstanceGetter()?.container;
        if (container) {
          let height = container.clientHeight;
          this.resizeObserver = new ResizeObserver((el: ResizeObserverEntry[]) => {
            const [entry] = el;
            // при скроле заметки высота может меняться на несколько пикселей,
            // поэтому подскроливаем к блоту только если высота меняется более чем на 15 пикселей
            if (Math.abs(height - entry.contentRect.height) > 15) {
              if (this.scope.threadContext) {
                const {
                  threadContext: { targetId, targetType },
                } = this.scope;

                if (targetId && this.scope.threadContext && this.targetNode) {
                  this.scrollToBlot(this.targetNode, targetId, targetType);
                }
              }
              height = entry.contentRect.height;
            }
          });
          this.resizeObserver.observe(container);
        }
      }
    }
  };

  public createInlineComment = async (blotId: string): Promise<{ threadId: string; isNew: boolean }> => {
    document.querySelectorAll('.temporary-inline-thread-node-origin').forEach((node) => node.remove());

    this.openComments(blotId, {
      target: 'text',
      text: '',
      ids: [],
      threadId: null,
      targetParams: null,
    });

    return new Promise((resolve) => {
      this.threadResolver = (threadId) => {
        const openResult = { threadId, isNew: true };
        resolve(openResult);
      };
    });
  };

  onDocumentMouseDown = (event: globalThis.MouseEvent) => {
    const node = event.target as Node | undefined;
    const overlayContainers = document.querySelectorAll('.cdk-overlay-container');
    if (node) {
      const overlayContainer = Object.values(overlayContainers).find((container) => container.contains(node));
      if (node && node !== this.popupContainer && !this.popupContainer?.contains(node) && !overlayContainer) {
        this.destroyExistedPopup();
      }
    }
  };

  broadcastThreadReadEvent = (threadId: string): void => {
    const threadReadApiEvent = new CustomEvent('ng2:thread:read', {
      detail: {
        userId: this.user.id,
        workspaceId: this.workspaceId,
        noteId: this.noteId,
        threadId,
      },
    });

    document.dispatchEvent(threadReadApiEvent);
  };

  setThreadRead = (threadId: string | null): void => {
    if (!this.gatewayUrl || !this.sessionId || !threadId || !this.workspaceId) {
      return;
    }

    axios
      .post(
        `${this.gatewayUrl}/ft:comments/comments/read?workspace=${this.workspaceId}&thread=${threadId}`,
        { readUnread: true },
        { headers: { 'everhelper-session-id': this.sessionId } }
      )
      .then(() => {
        this.broadcastThreadReadEvent(threadId);
        console.log(`Thread ${threadId} messages has been read`);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  private destroyExistedPopup() {
    if (this.popupContainer?.childNodes.length) {
      for (const node of Array.from(this.popupContainer.childNodes)) {
        this.popupContainer.removeChild(node);
      }
    }
    document.removeEventListener('mousedown', this.onDocumentMouseDown, false);
    this.resizeObserver?.disconnect();
  }

  private onClosePopup = () => {
    if (this.scope.threadContext && this.scope.threadContext.threadId) {
      this.setThreadRead(this.scope.threadContext.threadId);
    }
    this.destroyExistedPopup();
  };

  private onPrevThread = () => {
    this.navigate(-1);
  };

  private onNextThread = () => {
    this.navigate(1);
  };

  private navigate(direction: number) {
    const searchCollection = this.getEditorExistedThreads();
    const currId = this.scope.threadContext?.threadId;
    const currIdx = searchCollection.findIndex((info) => info.threadId === currId);
    let nextIdx = 0;

    if (direction === 1) {
      if (currIdx === searchCollection.length - 1) {
        nextIdx = 0;
      } else {
        nextIdx = currIdx + 1;
      }
    }

    if (direction === -1) {
      if (currIdx) {
        nextIdx = currIdx - 1;
      } else {
        nextIdx = searchCollection.length - 1;
      }
    }

    const nextThreadInfo = searchCollection[nextIdx];
    const nextThreadId = nextThreadInfo.threadId;
    let nextThreadTitle: string;
    if (nextThreadInfo.target === 'text') {
      nextThreadTitle = this.editorInstanceGetter()?.plugins.commentManager?.getThreadPreview(nextThreadId)?.text || '';
    } else if (nextThreadInfo.target === 'cell') {
      nextThreadTitle = this.getTableCellText(nextThreadInfo.threadId);
    } else {
      nextThreadTitle = this.getRootBlotText(nextThreadInfo.threadId);
    }

    const nextThreadTarget = nextThreadInfo.target || 'blot';

    this.scope.threadContext = {
      targetId: nextThreadInfo.blotId,
      noteId: this.noteId,
      title: nextThreadTitle,
      targetType: nextThreadTarget,
      threadId: nextThreadId,
    };

    this.updatePopup();
  }

  private isRegisteredCommentsComponent(): boolean {
    return document.createElement('webnotes-note-comments').constructor !== HTMLElement;
  }

  private async updatePopup() {
    this.destroyExistedPopup();

    const editor = this.editorInstanceGetter();
    const selectedRange = editor?.scroll.selection.getRange()?.[1] || null;
    const selectedBlot = editor?.scroll.selectedBlocks?.[0] || null;
    const relatedBlot = editor?.findById(this.scope.threadContext?.targetId as string) || null;
    const range = {
      selectedRange,
      selectedBlot,
      relatedBlot,
    };

    if (!this.isRegisteredCommentsComponent()) {
      const theme = this.editorInstanceGetter()?.options.theme || 'light';
      const p = document.createElement('div');
      p.innerHTML = 'Comments are loading, please wait...';
      p.style.font = '400 14px / 19.88px Open Sans, sans-serif';
      p.style.background = theme === 'dark' ? '#363636' : 'white';
      p.style.color = theme === 'dark' ? 'white' : 'black';
      p.style.boxShadow = theme === 'dark' ? '0 4px 11px #1a23311f' : '0 4px 11px #1a23311f';
      p.style.borderRadius = '20px';
      p.style.padding = '20px 40px';
      this.popupContainer?.appendChild(p);
      this.movePopup(p, range);
      p.style.opacity = '1';
      await new Promise((resolve) => {
        const interval = setInterval(() => {
          if (this.isRegisteredCommentsComponent()) {
            p.remove();
            this.initPluginOnEditorAvailable();
            clearInterval(interval);
            resolve(null);
          }
        }, 300);
      });
    }

    const commentsPopup = document.createElement('webnotes-note-comments');

    if (this.readonly) {
      commentsPopup.setAttribute('data-comments-readonly', 'true');
    }
    Object.assign(commentsPopup, {
      apiGatewayUrl: this.gatewayUrl,
      apiGatewayToken: this.sessionId,
      staticAssetUrlPrefix: this.staticAssetPrefix,
      workspaceId: this.workspaceId,
      context: this.scope.context,
      threadContext: this.scope.threadContext,
      nimReadOnly: this.readonly,
      nimAttachmentsEnabled: true,
      hasNavigation: true,
    });
    commentsPopup.addEventListener('nimClose', this.onClosePopup);
    commentsPopup.addEventListener('nimPrev', this.onPrevThread);
    commentsPopup.addEventListener('nimNext', this.onNextThread);
    commentsPopup.addEventListener('nimResolveThread', (event: Event) => {
      const customEvent = event as CustomEvent<ResoveThreadEvent>;
      this.threadResolver(customEvent.detail.threadId);
    });
    commentsPopup.addEventListener('nimDeleteThread', () => {
      commentsPopup.remove();
    });

    commentsPopup.style.visibility = 'hidden';
    this.popupContainer?.appendChild(commentsPopup);
    this.movePopup(commentsPopup, range);
    commentsPopup.style.visibility = 'visible';
    commentsPopup.style.opacity = '1';
    setTimeout(() => {
      commentsPopup?.querySelector('textarea')?.focus();
    }, 0);
    document.addEventListener('mousedown', this.onDocumentMouseDown, false);
  }

  private scrollToBlot(targetNode: HTMLElement, targetId: string, targetType: string | null) {
    if (!this.isElementInEditorViewport(targetNode)) {
      if (targetType !== 'cell') {
        this.editorInstanceGetter()?.plugins.scrollSaver?.scrollToBlot(targetId, false, false);
      } else {
        targetNode.scrollIntoView();
      }
    }
  }

  private movePopup(commentsPopup: HTMLElement, range?: SelectedBlotRange) {
    const editor = this.editorInstanceGetter();
    if (this.scope.threadContext) {
      const {
        threadContext: { threadId, targetId, targetType },
      } = this.scope;

      const targetNode = this.getNodeForThread(
        threadId as string,
        this.scope.threadContext.targetParams,
        range
      ) as HTMLElement;

      if (targetNode) {
        this.targetNode = targetNode;

        const isBlotThread = targetType !== 'text';
        const isImageBlotThread = targetNode.classList.contains('image-container');

        const placement = 'bottom-start';
        const offset = { x: 0, y: 0 };
        const beautifulPadding = 25;

        if (isBlotThread) {
          offset.y = -targetNode.clientHeight + beautifulPadding;
          if (isImageBlotThread) {
            offset.x = beautifulPadding;
          }
        }

        if (threadId) {
          this.scrollToBlot(targetNode, targetId, targetType);
        }

        createPopper(targetNode, commentsPopup, {
          placement,
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [offset.x, offset.y],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: editor?.getScrollingContainer(),
                altAxis: true,
              },
            },
          ],
        });
        commentsPopup.style.opacity = '0';
      }
    }
  }

  private getNodeForThread(
    threadId: string,
    targetParams: ThreadTargetParams | null = null,
    range?: SelectedBlotRange
  ) {
    const threadInfo = this.threadsInfo?.find((info) => info.threadId === threadId);
    let targetNode;

    if (!threadInfo) {
      const { selectedRange, selectedBlot, relatedBlot } = range || {};

      const isNoRange = !selectedRange?.start.offset && !selectedRange?.end.offset;
      const isJustCursor = selectedRange?.start.offset === selectedRange?.end.offset;

      if (isNoRange || isJustCursor) {
        console.log('detect node for new blot thread...');
        if (selectedBlot) {
          console.log('using node from "selectedBlocks"');
          if (
            selectedBlot.constructor.blotName === 'table' &&
            targetParams &&
            targetParams.type === 'tableCell' &&
            targetParams.position
          ) {
            if (selectedBlot.getCellLink) {
              targetNode = selectedBlot.getCellLink(targetParams.position).getNode();
            }
          } else {
            if (selectedBlot.getCommentsAnchorNode) {
              targetNode = selectedBlot.getCommentsAnchorNode();
            } else {
              targetNode = selectedBlot.domNode;
            }
          }
        } else if (relatedBlot) {
          console.log('using related node from "threadContext');
          if (relatedBlot.getCommentsAnchorNode) {
            targetNode = relatedBlot.getCommentsAnchorNode();
          } else {
            targetNode = relatedBlot.domNode;
          }
        } else {
          console.log('ERROR! No node for blot:', this.scope.threadContext?.targetId);
        }
      } else {
        console.log('create node for new inline thread...');
        const containerNode = document.querySelector<HTMLDivElement>('body');
        if (containerNode) {
          targetNode = this.createTemporaryNodeByRangeSelection(selectedRange.native, containerNode);
          containerNode.appendChild(targetNode);
        }
      }
    } else {
      if (threadInfo?.target === 'blot' || threadInfo?.target === 'image-point') {
        targetNode = this.getRootBlotNode(threadId);
      }

      if (threadInfo?.target === 'text') {
        targetNode = this.getInlineBlotNode(threadId);
      }

      if (threadInfo?.target === 'cell') {
        targetNode = this.getTableCellNode(threadId);
      }

      if (targetNode && targetNode.classList.contains('image')) {
        targetNode = targetNode.querySelector('.image-container');
      }
    }

    return targetNode;
  }

  private createTemporaryNodeByRangeSelection(range: Range, offsetContainerNode: HTMLElement) {
    const offsetContainerRect = offsetContainerNode.getBoundingClientRect();
    const targetOriginRect = range.getBoundingClientRect();
    const targetOriginNode = document.createElement('div');
    targetOriginNode.classList.add(this.TEMP_INLINE_THREAD_CLASS);

    Object.assign(targetOriginNode.style, {
      background: 'rgba(255, 255, 0, 0)',
      position: 'absolute',
      zIndex: '999',
      pointerEvents: 'none',
      top: `${targetOriginRect.top - offsetContainerRect.top + offsetContainerNode.scrollTop}px`,
      left: `${targetOriginRect.left - offsetContainerRect.left}px`,
      width: `${targetOriginRect.width}px`,
      height: `${targetOriginRect.height}px`,
    });

    return targetOriginNode;
  }

  private getRootBlot(threadId: string) {
    if (this.threadsInfo) {
      const threadInfo = this.threadsInfo.find((info) => info.threadId === threadId);
      if (threadInfo) {
        return this.editorInstanceGetter()?.findById(threadInfo.blotId);
      }
    }
    return null;
  }

  private getRootBlotNode(threadId: string) {
    const rootBlot = this.getRootBlot(threadId);
    if (rootBlot && rootBlot.getCommentsAnchorNode) {
      return rootBlot.getCommentsAnchorNode();
    }
    return rootBlot?.domNode;
  }

  private getRootBlotText(threadId: string) {
    return this.getRootBlot(threadId)?.getText?.() || '';
  }

  private getInlineBlotNode(threadId: string) {
    return document.querySelector<HTMLElement & { __blot: Blot }>(`comment[data-comment-threadid="${threadId}"]`);
  }

  private getTableCellRef(threadId: string) {
    return this.editorInstanceGetter()?.plugins.commentManager?.getTableCellNodeByThreadId(threadId);
  }

  private getTableCellNode(threadId: string) {
    const cellRef = this.getTableCellRef(threadId);
    return (cellRef && cellRef.getNode && cellRef.getNode()) || null;
  }

  private getTableCellText(threadId: string): string {
    const cellRef = this.getTableCellRef(threadId);
    return (cellRef && cellRef.getText && cellRef.getText()) || '';
  }

  private isElementInEditorViewport(el: HTMLElement) {
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  private getEditorExistedThreads() {
    const editor = this.editorInstanceGetter();
    if (editor) {
      const editorComments = editor.plugins.commentManager?.comments;
      if (editorComments && editorComments.length) {
        console.log(editorComments);
        return editorComments.filter((thread) => !thread.notFound);
      }
    }
    return [];
  }

  public formatToCommentThread = async (blotsFormat: { blotId: string; params: unknown[] }[]) => {
    if (this.editorInstanceGetter()?.mode === 'readonly') {
      this.refreshTreads();
      if (this.shareId) {
        try {
          await axios.post(
            `${this.gatewayUrl}/ft:share/${this.shareId}/formatBlots`,
            {
              securityKey: this.securityKey,
              blots: blotsFormat,
            },
            {
              headers: { 'everhelper-session-id': this.sessionId },
            }
          );
        } catch (err) {
          //do nothing
        }
      }
      return;
    }
  };
}
